// Semantic diff between two Control Plane documents.
//
// A JSON diff is not a review surface. An operator about to change what every
// installed Gao app renders needs to know four things, and "line 47 changed" is
// none of them: which rows appear, which disappear, which fields moved, and —
// the one people actually get wrong — which rows silently stop rendering on
// some devices without being removed at all.
//
// That last class is why VisibilityChange exists separately from FieldChange.
// Setting `minAppVersion` or flipping `environment` is a one-word edit that
// deletes a row from a population of devices. It looks identical in a JSON diff
// to renaming a category.

import {
  allItems,
  decideItemVisibility,
  HIDDEN_REASON_TEXT,
  inRenderOrder,
  modelItems,
  type CatalogItem,
  type ControlPlaneDocument,
  type ItemEnvironment,
  type Lane,
  type VisibilityDecision,
} from "./contract";
import { humanBytes, type LocalAIModelItem } from "./localAI";

export const DIFFABLE_FIELDS = [
  "title",
  "description",
  "category",
  "url",
  "environment",
  "enabled",
  "sortOrder",
  "iconUrl",
  "requiresAuth",
  "minAppVersion",
] as const;

export type DiffableField = (typeof DIFFABLE_FIELDS)[number];

/** A `local_ai_models` row's reviewable fields: what a person reads (name, one
 *  line, order), where it is served (environment), what it means on a device
 *  (state, reason) — and the manifest digest, which must never change under
 *  one id (a changed package is a NEW release). */
export const MODEL_DIFFABLE_FIELDS = [
  "displayName",
  "shortDescription",
  "state",
  "reason",
  "environment",
  "order",
  "preference",
  "manifestSha256",
] as const;

export interface FieldChange {
  field: DiffableField | (typeof MODEL_DIFFABLE_FIELDS)[number];
  before: unknown;
  after: unknown;
}

/**
 * One reviewable row, whichever component it came from. The review surfaces
 * read only these members, so a model list reviews exactly like a catalog.
 */
export interface DiffRow {
  id: string;
  title: string;
  /** Catalog: the url a device opens. Model: the release and its size. */
  url: string;
  environment: ItemEnvironment;
  sortOrder: number;
  minAppVersion: string | null;
  /** The fields compared for "modified". */
  values: Record<string, unknown>;
  /** A model row: its state, so visibility follows the model law. */
  model: LocalAIModelItem | null;
  catalog: CatalogItem | null;
}

export function catalogRow(i: CatalogItem): DiffRow {
  const values: Record<string, unknown> = {};
  for (const f of DIFFABLE_FIELDS) values[f] = i[f];
  return {
    id: i.id, title: i.title, url: i.url, environment: i.environment, sortOrder: i.sortOrder,
    minAppVersion: i.minAppVersion, values, model: null, catalog: i,
  };
}

export function modelRow(i: LocalAIModelItem): DiffRow {
  const p = i.release.presentation;
  return {
    id: i.id,
    title: p.displayName,
    url: `${i.release.modelId}@${i.release.version} · ${humanBytes(i.release.package.downloadBytes)} · ${i.state}`,
    environment: i.environment,
    sortOrder: p.order,
    minAppVersion: null,
    values: {
      displayName: p.displayName,
      shortDescription: p.shortDescription,
      state: i.state,
      reason: i.reason,
      environment: i.environment,
      order: p.order,
      preference: p.preference,
      manifestSha256: i.release.package.manifest.sha256,
    },
    model: i,
    catalog: null,
  };
}

/** Every reviewable row of a document, in the order a device renders them. */
export function rowsOf(doc: ControlPlaneDocument): DiffRow[] {
  const models = modelItems(doc).map(modelRow);
  const grid = inRenderOrder(allItems(doc)).map(catalogRow);
  return [...grid, ...models.sort((a, b) => (a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.id < b.id ? -1 : 1))];
}

/** Whether a row reaches a device on `lane`: the catalog policy, or — for a
 *  model — the lane rule alone (the server's projection, §9.2). */
export function rowVisibility(row: DiffRow, lane: Lane, clientVersion: string | null): VisibilityDecision {
  if (row.catalog) return decideItemVisibility(row.catalog, lane, clientVersion);
  const allowed = lane === "production" ? ["production"] : ["production", "staging", "test"];
  return allowed.includes(row.environment) ? { visible: true } : { visible: false, reason: "environment_mismatch" };
}

export interface AddedRow {
  kind: "added";
  item: DiffRow;
}
export interface RemovedRow {
  kind: "removed";
  item: DiffRow;
}
export interface ModifiedRow {
  kind: "modified";
  before: DiffRow;
  after: DiffRow;
  changes: FieldChange[];
}
export interface ReorderedRow {
  kind: "reordered";
  item: DiffRow;
  fromPosition: number;
  toPosition: number;
}

export type RowChange = AddedRow | RemovedRow | ModifiedRow | ReorderedRow;

export interface VisibilityChange {
  itemId: string;
  title: string;
  lane: Lane;
  wasVisible: boolean;
  isVisible: boolean;
  /** Why it is hidden AFTER the change, when it is. */
  reason: string | null;
}

export interface DocumentDiff {
  added: AddedRow[];
  removed: RemovedRow[];
  modified: ModifiedRow[];
  reordered: ReorderedRow[];
  /** Every row whose renderability changes on ANY lane, computed per lane. */
  visibility: VisibilityChange[];
  /** True when nothing at all differs. */
  identical: boolean;
}

function byId(items: DiffRow[]): Map<string, DiffRow> {
  return new Map(items.map((i) => [i.id, i]));
}

function fieldChanges(before: DiffRow, after: DiffRow): FieldChange[] {
  const out: FieldChange[] = [];
  const fields = (after.model ? MODEL_DIFFABLE_FIELDS : DIFFABLE_FIELDS) as readonly FieldChange["field"][];
  for (const field of fields) {
    if (before.values[field] !== after.values[field]) {
      out.push({ field, before: before.values[field], after: after.values[field] });
    }
  }
  return out;
}

/**
 * `sortOrder` is reported as a REORDER, not as a field change, whenever the
 * row's rendered position moves — and is suppressed entirely when it does not.
 * Rewriting every row's sortOrder to close a gap is a routine editor operation
 * that changes ten numbers and moves nothing; reporting ten field changes for
 * it would bury the one row that actually moved.
 */
function positions(items: DiffRow[]): Map<string, number> {
  const ordered = [...items].sort((a, b) => (a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return new Map(ordered.map((i, idx) => [i.id, idx]));
}

export function diffDocuments(
  before: ControlPlaneDocument,
  after: ControlPlaneDocument,
  lanes: readonly Lane[] = ["production", "test", "dev"],
): DocumentDiff {
  const beforeItems = rowsOf(before);
  const afterItems = rowsOf(after);
  const beforeMap = byId(beforeItems);
  const afterMap = byId(afterItems);
  const beforePos = positions(beforeItems);
  const afterPos = positions(afterItems);

  const added: AddedRow[] = [];
  const removed: RemovedRow[] = [];
  const modified: ModifiedRow[] = [];
  const reordered: ReorderedRow[] = [];

  for (const item of afterItems) {
    const prior = beforeMap.get(item.id);
    if (!prior) {
      added.push({ kind: "added", item });
      continue;
    }
    const changes = fieldChanges(prior, item).filter(
      // sortOrder is handled by the reorder pass below.
      (c) => c.field !== "sortOrder",
    );
    if (changes.length > 0) {
      modified.push({ kind: "modified", before: prior, after: item, changes });
    }
    const from = beforePos.get(item.id);
    const to = afterPos.get(item.id);
    if (from !== undefined && to !== undefined && from !== to) {
      reordered.push({ kind: "reordered", item, fromPosition: from, toPosition: to });
    }
  }

  for (const item of beforeItems) {
    if (!afterMap.has(item.id)) removed.push({ kind: "removed", item });
  }

  // ── Visibility, per lane ──────────────────────────────────────────────────
  const visibility: VisibilityChange[] = [];
  for (const lane of lanes) {
    // A version-gated row is reported against a client that DECLARES a
    // version, because "hidden from a client that declares nothing" is true of
    // every gated row and would drown the signal. The console shows the
    // declared-version case; the editor's preview covers specific versions.
    const probeVersion = "999999.0.0";
    const ids = new Set([...beforeMap.keys(), ...afterMap.keys()]);
    for (const id of ids) {
      const b = beforeMap.get(id);
      const a = afterMap.get(id);
      const was = b ? rowVisibility(b, lane, probeVersion).visible : false;
      const isDecision = a ? rowVisibility(a, lane, probeVersion) : null;
      const is = isDecision ? isDecision.visible : false;
      if (was === is) continue;
      visibility.push({
        itemId: id,
        title: (a ?? b)!.title,
        lane,
        wasVisible: was,
        isVisible: is,
        reason:
          isDecision && !isDecision.visible
            ? HIDDEN_REASON_TEXT[isDecision.reason]
            : a
              ? null
              : "row removed",
      });
    }
  }

  return {
    added,
    removed,
    modified,
    reordered,
    visibility,
    identical:
      added.length === 0 &&
      removed.length === 0 &&
      modified.length === 0 &&
      reordered.length === 0,
  };
}

/** `null` renders as an em dash, booleans as on/off — a diff that shows
 *  `false` and `null` as bare words reads as if a field were deleted. */
export function renderValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (v === true) return "on";
  if (v === false) return "off";
  return String(v);
}

/** One-line summary for a list row or a confirmation dialog. */
export function summarize(d: DocumentDiff): string {
  if (d.identical) return "no changes";
  const parts: string[] = [];
  if (d.added.length) parts.push(`+${d.added.length} added`);
  if (d.removed.length) parts.push(`−${d.removed.length} removed`);
  if (d.modified.length) parts.push(`~${d.modified.length} modified`);
  if (d.reordered.length) parts.push(`↕${d.reordered.length} reordered`);
  return parts.join(" · ");
}
